<template>
  <div class="profile-container">
    <div v-if="loading" class="loading">Loading Profile...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else class="profile-content">
      
      <div class="profile-header">
        <div class="avatar-placeholder">{{ stats.username[0].toUpperCase() }}</div>
        <div class="header-info">
          <h1>{{ stats.username }}</h1>
          <p class="subtitle">Joined: {{ new Date(stats.joined).toLocaleDateString() }}</p>
          <div class="level-badge">Level {{ stats.level || 1 }}</div>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card">
           <h3>Kanji Started</h3>
           <p class="big-number">{{ stats.kanjiLearned }}</p>
        </div>
        <div class="stat-card">
           <h3>Kana Learned</h3>
           <p class="big-number">{{ stats.kanaLearned || 0 }}</p>
        </div>
      </div>

      <div class="srs-breakdown">
         <h2>SRS Distribution</h2>
         <div class="srs-bars">
            <div class="srs-item" v-for="(count, stage) in stats.srsDistribution" :key="stage">
               <div :class="['bar', stage]" :style="{ height: getBarHeight(count) + 'px' }"></div>
               <span class="count">{{ count }}</span>
               <span class="label">{{ capitalize(stage) }}</span>
            </div>
         </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const stats = ref(null);
const loading = ref(true);
const error = ref(null);

onMounted(async () => {
  const token = localStorage.getItem('token');
  const targetId = route.params.id; // If present, we are viewing someone else (Admin only)

  let url = '/api/progress/stats';
  if (targetId) {
      url = `/api/admin/users/${targetId}/stats`;
  }

  try {
    const res = await axios.get(url, {
       headers: { 'x-auth-token': token }
    });
    stats.value = res.data;
  } catch (e) {
    if (e.response && e.response.status === 403) {
        error.value = "Access Denied";
    } else {
        error.value = "Failed to load profile";
    }
  } finally {
    loading.value = false;
  }
});

function getBarHeight(count) {
    // scale 0-100 based on standard max
    if (count === 0) return 4;
    return Math.min(150, Math.max(20, count * 5));
}

function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
</script>

<style scoped>
.profile-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Segoe UI', sans-serif;
}
.profile-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  padding: 2rem;
}
.profile-header {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 3rem;
  border-bottom: 2px solid #f0f2f5;
  padding-bottom: 2rem;
}
.avatar-placeholder {
  width: 100px;
  height: 100px;
  background: #2a3a4a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  font-weight: bold;
}
.header-info h1 {
  margin: 0;
  color: #2a3a4a;
}
.subtitle {
  color: #888;
  margin: 0.5rem 0;
}
.level-badge {
  background: #f59e0b;
  color: white;
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
}
.stat-card {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
}
.big-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #4f8cff;
  margin: 0;
}

.srs-breakdown {
  margin-top: 2rem;
}
.srs-bars {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 200px;
  padding-top: 2rem;
}
.srs-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.bar {
  width: 40px;
  border-radius: 8px 8px 0 0;
  transition: height 1s ease;
}
.bar.apprentice { background: #f472b6; }
.bar.guru { background: #818cf8; }
.bar.master { background: #34d399; }
.bar.enlightened { background: #60a5fa; }
.bar.burned { background: #fbbf24; }

.count { font-weight: bold; }
.label { font-size: 0.8rem; color: #666; }

@media (max-width: 600px) {
  .profile-header {
    flex-direction: column;
    text-align: center;
  }
}
</style>
